import { type SignInResult, signInClient, signInErrorMessage } from "@serophin/shared";
import { type MutableRef, useEffect, useRef, useState } from "preact/hooks";

export type UseMagicLinkSignInOptions = {
  onSignedIn: (result: SignInResult) => void | Promise<void>;
  baseUrl?: string;
  param?: string;
};

export type MagicLinkSignInStatus = "verifying" | "failed";

export type MagicLinkSignIn = {
  status: MagicLinkSignInStatus;
  error: string | null;
};

type MagicLinkHandlers = Pick<UseMagicLinkSignInOptions, "onSignedIn" | "baseUrl">;

const verifyToken = async (token: string, handlers: MutableRef<MagicLinkHandlers>, fail: (message: string) => void) => {
  try {
    const result = await signInClient.verifyLink(token, { baseUrl: handlers.current.baseUrl });
    await handlers.current.onSignedIn(result);
  } catch (error) {
    fail(signInErrorMessage(error, "link"));
  }
};

export const useMagicLinkSignIn = ({ onSignedIn, baseUrl, param = "token" }: UseMagicLinkSignInOptions): MagicLinkSignIn => {
  const handlers = useRef<MagicLinkHandlers>({ onSignedIn, baseUrl });
  handlers.current = { onSignedIn, baseUrl };
  const verified = useRef<string | null>(null);
  const [state, setState] = useState<MagicLinkSignIn>({ status: "verifying", error: null });

  useEffect(() => {
    const fail = (message: string) => setState({ status: "failed", error: message });
    const token = new URLSearchParams(window.location.search).get(param);
    if (!token) {
      fail(signInErrorMessage(undefined, "link"));
      return;
    }
    if (verified.current === token) {
      return;
    }
    verified.current = token;
    setState({ status: "verifying", error: null });
    void verifyToken(token, handlers, fail);
  }, [param]);

  return state;
};
